import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Snackbar,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { Close, ContentCopy, PersonOutline, AdminPanelSettings, Visibility } from "@mui/icons-material";
import { PlatformUser } from "types/interfaces/adminUsers";
import CustomScrollbars from "../CustomScrollbars";
import { useEffect, useState } from "react";
import {
  useUpdateAdminUserMutation,
  useResetAdminUserPasswordMutation,
  useCreateAdminUserMutation,
  useArchiveAdminUserMutation,
  useGenerateAdminUserPasswordMutation,
  useUpdateAdminUserPermissionsMutation,
} from "features/adminUsers/adminUsers";
import { useAppDispatch, useAppSelector } from "features/store";
import { api } from "features/api";
import { setClientView } from "features/clientView/clientViewSlice";
import { useNavigate } from "react-router-dom";
import UserManagementChatBotTab from "./UserManagementChatBotTab";
import UserManagementOwnedFoldersTab from "./UserManagementOwnedFoldersTab";
import UserManagementIntegrationsTab from "./UserManagementIntegrationsTab";

interface UserManagementPanelProps {
  user: PlatformUser | null;
  onClose: () => void;
  onUserUpdated: (user: PlatformUser) => void;
  onUserArchived?: () => void;
  drafts: Record<number, UserManagementDrafts>;
  onDraftsChange: (
    updater: (prev: Record<number, UserManagementDrafts>) => Record<number, UserManagementDrafts>,
  ) => void;
}

interface ToastState {
  open: boolean;
  message: string;
  severity: "success" | "error";
}

interface ConfirmState {
  open: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
}

export interface ConfirmAccessChangesSection {
  label: string;
  items: string[];
}

interface SaveConfirmState {
  open: boolean;
  sections: ConfirmAccessChangesSection[];
  onConfirm: () => void;
}

type TabId = "profile" | "folders" | "integrations" | "permissions" | "chatbot";

/** Draft cache per user: unsaved selections are kept when switching users. */
export interface UserManagementDrafts {
  folders?: Record<number, boolean>;
  integrations?: Record<number, boolean>;
  chatbot?: { chat_id: string; api_key: string };
  permissions?: { is_calibrate_manager: boolean };
  profile?: {
    roles: {
      is_data_storage_user: boolean;
      is_integration_dashboard_user: boolean;
      is_ai_agent_user: boolean;
    };
    requiresPasswordChange: boolean;
  };
}

function rolesFromUser(u: PlatformUser | null) {
  return {
    is_data_storage_user: u?.profile.is_data_storage_user ?? true,
    is_integration_dashboard_user: u?.profile.is_integration_dashboard_user ?? false,
    is_ai_agent_user: u?.profile.is_ai_agent_user ?? false,
  };
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function isValidEmail(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed === "") return true;
  return EMAIL_REGEX.test(trimmed);
}

export default function UserManagementPanel({
  user,
  onClose,
  onUserUpdated,
  onUserArchived,
  drafts,
  onDraftsChange,
}: UserManagementPanelProps) {
  const isCreateMode = user === null;
  const currentUser = useAppSelector((state) => state.auth.user);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const currentPP = currentUser?.platform_profile ?? (currentUser as any)?.profile;
  const isSuperUser = Boolean(currentPP?.is_superuser);
  const isManager = Boolean(currentPP?.is_calibrate_manager);
  const canEdit = isSuperUser;
  const canUseClientView = isSuperUser || isManager;

  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));
  const [tab, setTab] = useState<TabId>("profile");
  const [initialPasswordForUser, setInitialPasswordForUser] = useState<{ userId: number; password: string; label: string } | null>(null);
  const [toast, setToast] = useState<ToastState>({ open: false, message: "", severity: "success" });
  const [confirm, setConfirm] = useState<ConfirmState>({
    open: false,
    title: "",
    message: "",
    onConfirm: () => {},
  });
  const [saveConfirm, setSaveConfirm] = useState<SaveConfirmState>({
    open: false,
    sections: [],
    onConfirm: () => {},
  });

  const [firstName, setFirstName] = useState(user?.first_name ?? "");
  const [email, setEmail] = useState(user?.email ?? "");
  const [username, setUsername] = useState(user?.username ?? "");
  const [roles, setRoles] = useState(() => rolesFromUser(user));
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(!user?.profile.is_pass_reset);

  useEffect(() => {
    setFirstName(user?.first_name ?? "");
    setEmail(user?.email ?? "");
    setUsername(user?.username ?? "");
    setRoles((prev) => {
      if (user && drafts[user.id]?.profile?.roles) {
        return drafts[user.id]!.profile!.roles;
      }
      return rolesFromUser(user);
    });
    setRequiresPasswordChange(() => {
      if (user && drafts[user.id]?.profile?.requiresPasswordChange !== undefined) {
        return drafts[user.id]!.profile!.requiresPasswordChange;
      }
      return !user?.profile.is_pass_reset;
    });
    setTab("profile");
    setInitialPasswordForUser((prev) => {
      if (user == null) return null;
      if (prev != null && prev.userId !== user.id) return null;
      return prev;
    });
  }, [user]);

  const isEmailInvalid = (email ?? "").trim() !== "" && !isValidEmail(email ?? "");

  const hasProfileChanges =
    user != null &&
    !isEmailInvalid &&
    ((user.first_name ?? "").trim() !== firstName.trim() ||
      (user.email ?? "").trim() !== (email ?? "").trim() ||
      user.profile.is_data_storage_user !== roles.is_data_storage_user ||
      user.profile.is_integration_dashboard_user !== roles.is_integration_dashboard_user ||
      user.profile.is_ai_agent_user !== roles.is_ai_agent_user ||
      (user.profile.is_pass_reset === true) !== !requiresPasswordChange);

  const [updateUser, { isLoading: isSaving }] = useUpdateAdminUserMutation();
  const [createUser, { isLoading: isCreating }] = useCreateAdminUserMutation();
  const [resetPassword, { isLoading: isResetting }] = useResetAdminUserPasswordMutation();
  const [archiveUser, { isLoading: isArchiving }] = useArchiveAdminUserMutation();
  const [generatePassword, { isLoading: isGenerating }] = useGenerateAdminUserPasswordMutation();
  const [updatePermissions] = useUpdateAdminUserPermissionsMutation();

  const showToast = (message: string, severity: "success" | "error") => {
    setToast({ open: true, message, severity });
  };

  const effectiveIsDataStorage = roles.is_data_storage_user;
  const effectiveIsIntegration = roles.is_integration_dashboard_user;
  const effectiveIsAiAgent = roles.is_ai_agent_user;

  const handleTabChange = (_: unknown, value: TabId) => {
    if (value === "folders" && !isCreateMode && !effectiveIsDataStorage) {
      showToast("Enable Data Storage User role first.", "error");
      return;
    }
    if (value === "integrations" && !isCreateMode && !effectiveIsIntegration) {
      showToast("Enable Integration Dashboard User role first.", "error");
      return;
    }
    if (value === "chatbot" && !isCreateMode && !effectiveIsAiAgent) {
      showToast("Enable AI Agent User role first.", "error");
      return;
    }
    if (value === "permissions" && !isSuperUser) {
      showToast("Only Super Users can manage permissions.", "error");
      return;
    }
    setTab(value);
  };

  const openConfirm = (opts: { title: string; message: string; onConfirm: () => void }) => {
    setConfirm({ open: true, ...opts });
  };
  const closeConfirm = () => setConfirm((c) => ({ ...c, open: false }));

  const openSaveConfirm = (sections: ConfirmAccessChangesSection[], onConfirm: () => void) => {
    setSaveConfirm({ open: true, sections, onConfirm });
  };
  const closeSaveConfirm = () => setSaveConfirm((s) => ({ ...s, open: false }));

  const handleSave = async () => {
    if (!user) return;
    try {
      const updated = await updateUser({
        id: user.id,
        username,
        first_name: firstName,
        email,
        ...roles,
        is_pass_reset: !requiresPasswordChange,
      }).unwrap();
      onUserUpdated(updated);
      onDraftsChange((prev) => {
        const current = prev[updated.id];
        if (!current?.profile) return prev;
        const { profile, ...restForUser } = current;
        const next = { ...prev };
        if (Object.keys(restForUser).length === 0) {
          const { [updated.id]: _, ...restUsers } = next;
          return restUsers;
        }
        next[updated.id] = restForUser as UserManagementDrafts;
        return next;
      });
      showToast("User updated successfully.", "success");
    } catch {
      showToast("Failed to update user.", "error");
    }
  };

  const handleCreate = async () => {
    try {
      const created = await createUser({
        username,
        first_name: firstName,
        email,
        ...roles,
      }).unwrap();
      if (created.generated_password) {
        setInitialPasswordForUser({ userId: created.id, password: created.generated_password, label: "Initial Password" });
      }
      showToast("User created successfully.", "success");
      onUserUpdated(created);
    } catch (err: any) {
      const errors = err?.data?.errors;
      const msg = errors ? Object.values(errors).flat().join(" ") : "Failed to create user.";
      showToast(msg, "error");
    }
  };

  const handleResetPassword = () => {
    if (!user) return;
    openConfirm({
      title: "Reset Password",
      message: `Send a password reset email to ${user.email || user.username}?`,
      onConfirm: async () => {
        try {
          await resetPassword(user.id).unwrap();
          showToast("Password reset email sent.", "success");
        } catch {
          showToast("Failed to send reset email.", "error");
        }
      },
    });
  };

  const handleGeneratePassword = () => {
    if (!user) return;
    openConfirm({
      title: "Generate New Password",
      message: `Generate a new password for ${user.first_name || user.username}? The current password will be replaced immediately.`,
      onConfirm: async () => {
        try {
          const result = await generatePassword(user.id).unwrap();
          setInitialPasswordForUser({
            userId: user.id,
            password: result.generated_password,
            label: "Generated Password",
          });
          // Persist \"require password change\" immediately so there are no unsaved changes.
          const updated = await updateUser({
            id: user.id,
            is_pass_reset: false,
          }).unwrap();
          onUserUpdated(updated);
          setRequiresPasswordChange(true);
          showToast("Password generated.", "success");
        } catch {
          showToast("Failed to generate password.", "error");
        }
      },
    });
  };

  const handleArchive = () => {
    if (!user) return;
    const isArchived = user.profile.is_archived;
    openConfirm({
      title: isArchived ? "Un-Archive User" : "Archive User",
      message: isArchived
        ? `Restore ${user.first_name || user.username}? They will be able to log in again.`
        : `Archive ${user.first_name || user.username}? They will be unable to log in. Their data will not be deleted.`,
      onConfirm: async () => {
        try {
          await archiveUser(user.id).unwrap();
          showToast(isArchived ? "User un-archived." : "User archived.", "success");
          onUserArchived?.();
        } catch {
          showToast("Failed to update archive status.", "error");
        }
      },
    });
  };

  const updateDraft = (
    userId: number,
    key: keyof UserManagementDrafts,
    value: UserManagementDrafts[keyof UserManagementDrafts],
  ) => {
    onDraftsChange((prev) => ({ ...prev, [userId]: { ...prev[userId], [key]: value } }));
  };
  const clearDraft = (userId: number, key: keyof UserManagementDrafts) => {
    onDraftsChange((prev) => {
      const next = { ...prev };
      const u = next[userId];
      if (!u) return prev;
      const { [key]: _, ...rest } = u;
      if (Object.keys(rest).length === 0) {
        const { [userId]: __, ...restUsers } = next;
        return restUsers;
      }
      next[userId] = rest as UserManagementDrafts;
      return next;
    });
  };

  const syncProfileDraft = (nextRoles: typeof roles, nextRequiresPasswordChange: boolean) => {
    if (!user) return;
    const serverRoles = rolesFromUser(user);
    const serverRequiresPasswordChange = !user.profile.is_pass_reset;
    const isSame =
      serverRoles.is_data_storage_user === nextRoles.is_data_storage_user &&
      serverRoles.is_integration_dashboard_user === nextRoles.is_integration_dashboard_user &&
      serverRoles.is_ai_agent_user === nextRoles.is_ai_agent_user &&
      serverRequiresPasswordChange === nextRequiresPasswordChange;

    if (isSame) {
      clearDraft(user.id, "profile");
    } else {
      updateDraft(user.id, "profile", {
        roles: nextRoles,
        requiresPasswordChange: nextRequiresPasswordChange,
      });
    }
  };

  const handleSavePermissions = async () => {
    if (!user) return;
    const val = drafts[user.id]?.permissions?.is_calibrate_manager ?? user.profile.is_calibrate_manager;
    try {
      const updated = await updatePermissions({ id: user.id, is_calibrate_manager: val }).unwrap();
      onUserUpdated(updated);
      clearDraft(user.id, "permissions");
      showToast("Permissions updated.", "success");
    } catch {
      showToast("Failed to update permissions.", "error");
    }
  };

  const fullName = user
    ? [user.first_name, user.last_name].filter(Boolean).join(" ") || user.username
    : "New User";

  return (
    <>
      <Box
        sx={(theme: Theme) => ({
          width: { xs: "100%", sm: 380, md: 420 },
          maxWidth: "100%",
          minWidth: 0,
          flexShrink: 0,
          borderLeft: { xs: "none", sm: `1px solid ${theme.palette.divider}` },
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.paper",
          overflow: "hidden",
        })}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: 2,
                bgcolor: "rgba(31,93,107,0.10)",
                display: "grid",
                placeItems: "center",
                fontWeight: 900,
                fontSize: 14,
                color: "primary.main",
              }}
            >
              {isCreateMode ? <PersonOutline fontSize="small" /> : fullName[0]?.toUpperCase()}
            </Box>
            <Box>
              <Box display="flex" alignItems="center" gap={0.75}>
                <Typography variant="subtitle2" fontWeight={800} lineHeight={1.2}>
                  {fullName}
                </Typography>
                {user?.profile.is_superuser && (
                  <AdminPanelSettings sx={{ fontSize: 18, color: "primary.main" }} titleAccess="Super Admin" />
                )}
                {user?.profile.is_archived && (
                  <Chip
                    label="Archived"
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: 10,
                      fontWeight: 700,
                      bgcolor: "rgba(211,47,47,0.10)",
                      color: "error.dark",
                    }}
                  />
                )}
              </Box>
              {user && (
                <Typography variant="caption" color="text.secondary">
                  @{user.username}
                </Typography>
              )}
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            {!isCreateMode && canUseClientView && user && user.id !== currentUser?.id && !user.profile.is_archived && (
              <Tooltip title="View the platform as this client would see it.">
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  startIcon={<Visibility fontSize="small" />}
                  onClick={() => {
                    const displayName = [user.first_name, user.last_name].filter(Boolean).join(" ") || user.username;
                    dispatch(
                      setClientView({
                        userId: user.id,
                        displayName,
                        isChatBotAllowed: user.profile.is_ai_agent_user,
                        isIntegrationUser: user.profile.is_integration_dashboard_user,
                        isCalibrateAdmin: user.profile.is_calibrate_manager,
                      })
                    );
                    dispatch(api.util.resetApiState());
                    navigate(`/client-view/${user.id}`);
                  }}
                  sx={{ textTransform: "none", fontWeight: 600, fontSize: 11, px: 1, minWidth: "auto", borderRadius: 1.5 }}
                >
                  Client View
                </Button>
              </Tooltip>
            )}
            <IconButton size="small" onClick={onClose}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Tabs */}
        <Box
          sx={{
            minWidth: 0,
            borderBottom: "1px solid",
            borderColor: "divider",
            overflowX: "auto",
            overflowY: "hidden",
          }}
        >
          <Tabs
            value={tab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ minHeight: 44 }}
            TabIndicatorProps={{ style: { height: 2 } }}
          >
            <Tab value="profile" label="Profile" sx={{ minHeight: 44, fontSize: 13, textTransform: "none", fontWeight: 700, minWidth: "auto", px: 1.5 }} />
            {!isCreateMode && (
              <Tab
                value="folders"
                label="Folders"
                disabled={!effectiveIsDataStorage}
                sx={{ minHeight: 44, fontSize: 13, textTransform: "none", fontWeight: 700, minWidth: "auto", px: 1.5 }}
              />
            )}
            {!isCreateMode && (
              <Tab
                value="integrations"
                label={isNarrow ? "Integr." : "Integrations"}
                disabled={!effectiveIsIntegration}
                sx={{ minHeight: 44, fontSize: 13, textTransform: "none", minWidth: "auto", px: 1.5 }}
              />
            )}
            {!isCreateMode && (
              <Tab
                value="chatbot"
                label={isNarrow ? "AI" : "AI Agent"}
                disabled={!effectiveIsAiAgent}
                sx={{ minHeight: 44, fontSize: 13, textTransform: "none", minWidth: "auto", px: 1.5 }}
              />
            )}
            {!isCreateMode && isSuperUser && (
              <Tab
                value="permissions"
                label={isNarrow ? "Perms" : "Permissions"}
                sx={{ minHeight: 44, fontSize: 13, textTransform: "none", minWidth: "auto", px: 1.5 }}
              />
            )}
          </Tabs>
        </Box>

        {/* Tab Content */}

        {tab === "folders" && user && (
          <UserManagementOwnedFoldersTab
            userId={user.id}
            isDataStorageUser={effectiveIsDataStorage}
            canEdit={canEdit}
            isCalibrateManager={isManager && !isSuperUser}
            folderDraft={drafts[user.id]?.folders}
            onFolderDraftChange={(folderId, hasAccess, serverHasAccess) =>
              onDraftsChange((prev) => {
                const current = prev[user.id]?.folders ?? {};
                if (hasAccess === serverHasAccess) {
                  const { [folderId]: _, ...rest } = current;
                  if (Object.keys(rest).length === 0) {
                    const { folders: __, ...userRest } = prev[user.id] ?? {};
                    if (Object.keys(userRest).length === 0) {
                      const { [user.id]: ___, ...usersRest } = prev;
                      return usersRest;
                    }
                    return { ...prev, [user.id]: userRest as UserManagementDrafts };
                  }
                  return { ...prev, [user.id]: { ...prev[user.id], folders: rest } };
                }
                return {
                  ...prev,
                  [user.id]: { ...prev[user.id], folders: { ...current, [folderId]: hasAccess } },
                };
              })
            }
            onSaveSuccess={showToast}
            onSaveComplete={() => clearDraft(user.id, "folders")}
          />
        )}

        {tab === "integrations" && user && (
          <UserManagementIntegrationsTab
            userId={user.id}
            isIntegrationDashboardUser={effectiveIsIntegration}
            canEdit={canEdit}
            isCalibrateManager={isManager && !isSuperUser}
            integrationDraft={drafts[user.id]?.integrations}
            onIntegrationDraftChange={(matchId, hasAccess, serverHasAccess) =>
              onDraftsChange((prev) => {
                const current = prev[user.id]?.integrations ?? {};
                if (hasAccess === serverHasAccess) {
                  const { [matchId]: _, ...rest } = current;
                  if (Object.keys(rest).length === 0) {
                    const { integrations: __, ...userRest } = prev[user.id] ?? {};
                    if (Object.keys(userRest).length === 0) {
                      const { [user.id]: ___, ...usersRest } = prev;
                      return usersRest;
                    }
                    return { ...prev, [user.id]: userRest as UserManagementDrafts };
                  }
                  return { ...prev, [user.id]: { ...prev[user.id], integrations: rest } };
                }
                return {
                  ...prev,
                  [user.id]: { ...prev[user.id], integrations: { ...current, [matchId]: hasAccess } },
                };
              })
            }
            onSuccess={showToast}
            onSaveComplete={() => clearDraft(user.id, "integrations")}
          />
        )}

        {tab === "chatbot" && user && (
          <UserManagementChatBotTab
            userId={user.id}
            canEdit={canEdit}
            isCalibrateManager={isManager && !isSuperUser}
            chatbotDraft={drafts[user.id]?.chatbot}
            onChatbotDraftChange={(data, serverProfile) =>
              onDraftsChange((prev) => {
                const same =
                  serverProfile != null &&
                  (data.chat_id ?? "") === (serverProfile.chat_id ?? "") &&
                  (data.api_key ?? "") === (serverProfile.api_key ?? "");
                if (same) {
                  const { chatbot: __, ...userRest } = prev[user.id] ?? {};
                  if (Object.keys(userRest).length === 0) {
                    const { [user.id]: ___, ...usersRest } = prev;
                    return usersRest;
                  }
                  return { ...prev, [user.id]: userRest as UserManagementDrafts };
                }
                return { ...prev, [user.id]: { ...prev[user.id], chatbot: data } };
              })
            }
            onSuccess={showToast}
            onSaveComplete={() => clearDraft(user.id, "chatbot")}
          />
        )}

        {tab === "permissions" && user && isSuperUser && (
          <>
            <CustomScrollbars sx={{ flex: 1, minHeight: 0 }}>
              <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
                {user.profile.is_superuser && currentUser?.id === user.id ? (
                  <Typography variant="body2" color="text.secondary">
                    You have full access to all permissions as a Super Admin.
                  </Typography>
                ) : (
                  <>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={700}
                      textTransform="uppercase"
                      letterSpacing={0.6}
                      display="block"
                      mb={0.75}
                    >
                      Manager Access
                    </Typography>
                    <Box display="flex" alignItems="center" justifyContent="space-between" py={0.4}>
                      <Box>
                        <Typography variant="body2">Calibrate Manager</Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.4 }}>
                          Read-only access to User Management. Full access to all folders and integrations on the web platform.
                        </Typography>
                      </Box>
                      <Switch
                        size="small"
                        checked={drafts[user.id]?.permissions?.is_calibrate_manager ?? user.profile.is_calibrate_manager}
                        onChange={(e) => {
                          const val = e.target.checked;
                          if (val === user.profile.is_calibrate_manager) {
                            clearDraft(user.id, "permissions");
                          } else {
                            updateDraft(user.id, "permissions", { is_calibrate_manager: val });
                          }
                        }}
                        disabled={user.profile.is_superuser}
                      />
                    </Box>
                  </>
                )}
              </Box>
            </CustomScrollbars>
            {user && !user.profile.is_superuser && (
              <Box px={2} pb={2} pt={1} sx={{ flexShrink: 0 }}>
                {(() => {
                  const serverVal = user.profile.is_calibrate_manager;
                  const draftVal = drafts[user.id]?.permissions?.is_calibrate_manager;
                  const hasUnsaved = draftVal !== undefined && draftVal !== serverVal;
                  return (
                    <>
                      {hasUnsaved && (
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          display="block"
                          sx={{ mb: 1, color: "#ad1457" }}
                        >
                          Unsaved changes
                        </Typography>
                      )}
                      <Tooltip title={!hasUnsaved ? "No changes to save" : ""}>
                        <span>
                          <Button
                            variant="contained"
                            fullWidth
                            onClick={() => {
                              if (!hasUnsaved) return;
                              openSaveConfirm(
                                [{ label: "PERMISSIONS", items: ["Calibrate Manager"] }],
                                () => { closeSaveConfirm(); handleSavePermissions(); },
                              );
                            }}
                            disabled={!hasUnsaved}
                            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5, py: 0.9 }}
                          >
                            Save Changes
                          </Button>
                        </span>
                      </Tooltip>
                    </>
                  );
                })()}
              </Box>
            )}
          </>
        )}

        {tab === "profile" && (
          <>
            <CustomScrollbars sx={{ flex: 1, minHeight: 0 }}>
              <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
                {!isCreateMode && initialPasswordForUser?.userId === user?.id && (
                  <Box
                    sx={{
                      mb: 2,
                      p: 1.5,
                      borderRadius: 1.5,
                      bgcolor: "rgba(46,125,50,0.08)",
                      border: "1px solid rgba(46,125,50,0.2)",
                    }}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Typography variant="caption" fontWeight={700} color="success.dark" textTransform="uppercase" letterSpacing={0.6}>
                        {initialPasswordForUser.label}
                      </Typography>
                      <IconButton size="small" onClick={() => setInitialPasswordForUser(null)}>
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                    <TextField
                      value={initialPasswordForUser.password}
                      size="small"
                      fullWidth
                      inputProps={{ readOnly: true, style: { fontFamily: "monospace", fontWeight: 700 } }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => navigator.clipboard.writeText(initialPasswordForUser.password)}>
                              <ContentCopy fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                )}

                <TextField
                  label="Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  size="small"
                  fullWidth
                  required
                  disabled={!canEdit}
                  sx={{ mb: 1.25, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                />
                <TextField
                  label="Client Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  size="small"
                  fullWidth
                  required
                  disabled={!canEdit}
                  sx={{ mb: 1.25, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                />
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  size="small"
                  fullWidth
                  disabled={!canEdit}
                  error={isEmailInvalid}
                  helperText={isEmailInvalid ? "Enter a valid email address" : undefined}
                  sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                />

                {!isCreateMode && user && (
                  <Box
                    sx={{
                      mb: 2,
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 1.5,
                      bgcolor: "grey.50",
                      border: "1px dashed",
                      borderColor: "divider",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      Platform User ID
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                      {user.id}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ mb: 1.5 }} />

                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={700}
                  textTransform="uppercase"
                  letterSpacing={0.6}
                  display="block"
                  mb={0.75}
                >
                  Roles
                </Typography>
                <Box>
                  {(
                    [
                      { key: "is_data_storage_user", label: "Data Storage User" },
                      { key: "is_integration_dashboard_user", label: "Integration Dashboard User" },
                      { key: "is_ai_agent_user", label: "AI Agent User" },
                    ] as const
                  ).map(({ key, label }) => (
                    <Box key={key} display="flex" alignItems="center" justifyContent="space-between" py={0.4}>
                      <Typography variant="body2">{label}</Typography>
                      <Switch
                        size="small"
                        checked={roles[key]}
                        onChange={(e) => {
                          if (!canEdit) return;
                          const checked = e.target.checked;
                          setRoles((prev) => {
                            const next = { ...prev, [key]: checked };
                            syncProfileDraft(next, requiresPasswordChange);
                            return next;
                          });
                        }}
                        disabled={!canEdit}
                      />
                    </Box>
                  ))}
                </Box>

                {!isCreateMode && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={700}
                      textTransform="uppercase"
                      letterSpacing={0.6}
                      display="block"
                      mb={0.75}
                    >
                      Security
                    </Typography>
                    <Box display="flex" alignItems="center" justifyContent="space-between" py={0.4} sx={{ minWidth: 0 }}>
                      <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
                        <Typography variant="body2">Require password change</Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.4 }}>
                          User will be forced to set a new password on next login
                        </Typography>
                      </Box>
                      <Switch
                        size="small"
                        checked={requiresPasswordChange}
                        onChange={(e) => {
                          const val = e.target.checked;
                          setRequiresPasswordChange(val);
                          syncProfileDraft(roles, val);
                        }}
                        disabled={!canEdit && !isManager}
                        sx={{ flexShrink: 0 }}
                      />
                    </Box>
                  </>
                )}
              </Box>
            </CustomScrollbars>

            {/* Action Buttons */}
            <Box px={2} pb={2} pt={1} sx={{ display: "flex", flexDirection: "column", gap: 1, flexShrink: 0 }}>
              {isCreateMode ? (
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleCreate}
                  disabled={!canEdit || isCreating || !username.trim() || !firstName.trim() || isEmailInvalid}
                  sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5 }}
                >
                  {isCreating ? "Creating..." : "Create User"}
                </Button>
              ) : (
                <>
                  {hasProfileChanges && (
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      display="block"
                      sx={{ mb: 0.5, color: "#ad1457" }}
                    >
                      Unsaved changes
                    </Typography>
                  )}
                  <Tooltip title={!hasProfileChanges ? "No changes to save" : ""}>
                    <span>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => {
                          if (!hasProfileChanges || !user) return;
                          const items: string[] = [];
                          if ((user.first_name ?? "").trim() !== firstName.trim()) items.push("Client Name");
                          if ((user.email ?? "").trim() !== (email ?? "").trim()) items.push("Email Address");
                          if (user.profile.is_data_storage_user !== roles.is_data_storage_user) items.push("Data Storage User");
                          if (user.profile.is_integration_dashboard_user !== roles.is_integration_dashboard_user) items.push("Integration Dashboard User");
                          if (user.profile.is_ai_agent_user !== roles.is_ai_agent_user) items.push("AI Agent User");
                          if ((user.profile.is_pass_reset === true) !== !requiresPasswordChange) items.push("Require password change");
                          openSaveConfirm(
                            [{ label: "PROFILE", items }],
                            () => { closeSaveConfirm(); handleSave(); },
                          );
                        }}
                        disabled={!canEdit || isSaving || !hasProfileChanges}
                        sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5 }}
                      >
                        {isSaving ? "Saving..." : "Save Changes"}
                      </Button>
                    </span>
                  </Tooltip>
                  <Box sx={{ display: "flex", gap: 1, "& > *": { flex: 1 } }}>
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      onClick={handleResetPassword}
                      disabled={(!canEdit && !isManager) || isResetting || !user?.email}
                      sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5, fontSize: 12 }}
                    >
                      Reset Password
                    </Button>
                    <Tooltip title={user?.profile.is_superuser ? "Cannot generate password for Super Admin." : ""}>
                      <span>
                        <Button
                          variant="outlined"
                          size="small"
                          fullWidth
                          onClick={handleGeneratePassword}
                          disabled={(!canEdit && !isManager) || isGenerating || !!user?.profile.is_superuser}
                          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5, fontSize: 12 }}
                        >
                          {isGenerating ? "Generating..." : "Generate Password"}
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>
                  {user && !user.profile.is_superuser && user.id !== currentUser?.id && (
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      color={user.profile.is_archived ? "success" : "error"}
                      onClick={handleArchive}
                      disabled={!canEdit || isArchiving}
                      sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5, fontSize: 12 }}
                    >
                      {isArchiving
                        ? "Processing..."
                        : user.profile.is_archived
                          ? "Un-Archive User"
                          : "Archive User"}
                    </Button>
                  )}
                </>
              )}
            </Box>
          </>
        )}
      </Box>

      {/* Confirm Dialog */}
      <Dialog open={confirm.open} onClose={closeConfirm} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>{confirm.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirm.message}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeConfirm} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => { closeConfirm(); confirm.onConfirm(); }}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Access Changes Dialog */}
      <Dialog open={saveConfirm.open} onClose={closeSaveConfirm} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Confirm Access Changes</DialogTitle>
        <DialogContent>
          {saveConfirm.sections.map((section) => (
            <Box key={section.label} sx={{ mb: section.label ? 1.5 : 0 }}>
              {section.label && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={700}
                  textTransform="uppercase"
                  letterSpacing={0.6}
                  display="block"
                  sx={{ mb: 0.5 }}
                >
                  {section.label}
                </Typography>
              )}
              {section.items.map((item) => (
                <Typography key={item} variant="body2" component="div">
                  {item}
                </Typography>
              ))}
            </Box>
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeSaveConfirm} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => { closeSaveConfirm(); saveConfirm.onConfirm(); }}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))} sx={{ fontWeight: 600 }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}
